import {
  Dispatch,
  SetStateAction,
  createContext,
  ReactNode,
  useState,
  useContext,
  useEffect,
} from 'react';
import { useUser } from '@/contexts/AuthContext';
import { Toast } from '@/lib/Toast';
import { INotification } from '@/types/api/notifications';
import { handleErrorAsync } from '@/utils/handlers';
import { getAllNotifications } from '@/utils/http/notifications';
import { useLoading } from '../LoadingContext';

interface NotificationContextProps {
  children: ReactNode;
}

type NotificationContextData = {
  notifications: INotification[];
  setNotifications: Dispatch<SetStateAction<INotification[]>>;
  nextPage: string;
  setNextPage: Dispatch<SetStateAction<string>>;
};

export const NotificationContext = createContext<NotificationContextData>(
  {} as NotificationContextData
);

NotificationContext.displayName = 'NotificationContext';

export function NotificationProvider({ children }: NotificationContextProps) {
  const { isAuthenticated } = useUser();
  const { setIsLoading } = useLoading();
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [nextPage, setNextPage] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setNextPage('');
      return;
    }

    const getNotifications = async () => {
      setIsLoading(true);
      const [initialNotifications, error] = await handleErrorAsync(async () => {
        const result = await getAllNotifications({
          currentPage: 1,
          pageSize: 10,
          ordering: 'read, -created_at',
        });

        return result;
      }).finally(() => setIsLoading(false));

      if (error) return Toast('error', error.message);

      setNotifications(initialNotifications.notifications);

      setNextPage(initialNotifications.nextPage);
    };

    getNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <NotificationContext.Provider
      value={{ notifications, setNotifications, nextPage, setNextPage }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw Error('Must be inside a NotificationProvider');
  }

  return context;
};
